import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatPrice, propertyImage } from "@/lib/api";
import { useFavorites } from "@/context/FavoritesContext";

export default function Shortlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toggle, ids } = useFavorites();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/favorites");
      setItems(data.items);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, ids.length]);

  const rows = [
    ["Price", (p) => formatPrice(p.price, p.listing_type)],
    ["Type", (p) => (p.listing_type === "rent" ? "Rent" : p.listing_type === "pg" ? "PG" : "Sale")],
    ["Category", (p) => p.category],
    ["City", (p) => p.city],
    ["Locality", (p) => p.locality || "—"],
    ["Bedrooms", (p) => p.bedrooms || "—"],
    ["Bathrooms", (p) => p.bathrooms || "—"],
    ["Area (sqft)", (p) => p.area_sqft || "—"],
    ["Furnishing", (p) => p.furnishing || "—"],
    ["Amenities", (p) => (p.amenities?.length ? p.amenities.join(", ") : "—")],
    ["Contact", (p) => p.contact_phone || "—"],
  ];

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10">
      <h1 className="font-display text-3xl">Wishlist</h1>
      <p className="mt-1 text-[13px] text-muted-foreground">{items.length} saved properties</p>

      {loading ? (
        <p className="mt-8 text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-border p-10" data-testid="shortlist-empty">
          <Heart className="h-7 w-7 text-primary" strokeWidth={1.8} />
          <p className="mt-4 font-display text-xl">Nothing saved yet</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Tap the heart on any property to save it here and compare side by side.
          </p>
          <Link to="/properties">
            <Button className="mt-6 h-12 rounded-lg font-bold hover:brightness-95" data-testid="shortlist-browse-btn">
              Start exploring
            </Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-8 grid gap-x-3.5 gap-y-6 sm:gap-x-6 sm:gap-y-9 grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="shortlist-grid">
            {items.map((p) => (
              <div key={p.property_id}>
                <Link to={`/property/${p.property_id}`} className="block overflow-hidden rounded-xl">
                  <img src={propertyImage(p)} alt={p.title} className="h-48 w-full object-cover hover:scale-105 transition-transform duration-500" />
                </Link>
                <div className="pt-3">
                  <p className="font-display text-[15px] line-clamp-1">
                    {p.locality ? `${p.locality}, ` : ""}
                    {p.city}
                  </p>
                  <Link to={`/property/${p.property_id}`} className="mt-0.5 block text-[13px] text-muted-foreground line-clamp-1 hover:text-foreground transition-colors duration-200">
                    {p.title}
                  </Link>
                  <p className="mt-1.5 text-[15px] font-bold">{formatPrice(p.price, p.listing_type)}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 px-0 text-destructive underline"
                    onClick={() => toggle(p.property_id)}
                    data-testid={`shortlist-remove-${p.property_id}`}
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <section className="mt-16">
            <h2 className="font-display text-2xl">Compare side by side</h2>
            <div className="mt-5 rounded-2xl border border-border overflow-x-auto">
              <Table data-testid="compare-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[140px]">Detail</TableHead>
                    {items.map((p) => (
                      <TableHead key={p.property_id} className="min-w-[200px]">{p.title}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(([label, fn]) => (
                    <TableRow key={label}>
                      <TableCell className="text-xs font-bold">{label}</TableCell>
                      {items.map((p) => (
                        <TableCell key={p.property_id} className="text-sm capitalize">{String(fn(p))}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
